import { Col, Layout, Row } from 'antd';
import React, { ReactNode } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { Spinner } from '../components/loading/Spinner';
import '../style/App.less';
import { useAuthenticationLoading, useCurrentUser } from './authentication/AuthenticationContext';
import SignIn from './authentication/SignIn';
import Admin from './admin/Admin';
import DemoOverview from './demo/overview/DemoOverview';
import DemoResult from './demo/file/DemoResult';
import ModelOverview from './customization/models/ModelOverview';
import ModelDetail from './customization/model/ModelDetail';
import ReviewResult from './customization/file/ReviewResult';
import ProjectOverview from './datasets/project/ProjectOverview';
import DatasetOverview from './datasets/dataset/DatasetOverview';
import DatasetReport from './datasets/dataset/DatasetReport';
import FileOverview from './datasets/file/FileOverview';
import AlgorithmDetailOverview from './overview/AlgorithmDetailOverview';
import ScoreDatasetOverview from './overview/dataset/ScoreDatasetOverview';
import ScoreOverview from './overview/ScoreOverview';
import DetailTestResult from './overview/file/DetailTestResult';
import FlowOverview from './flow/flows/FlowOverview';
import FlowDetail from './flow/flow/FlowDetail';
import FlowInferenceResult from './flow/file/InferenceResult';
import ClientOverview from './inbox/client/ClientOverview';
import InboxsetOverview from './inbox/inbox/InboxsetOverview';
import InboxFileOverview from './inbox/file/InboxFileOverview';
import IntroductionPage from './IntroductionPage';
import SideBar from './navigation/SideBar';
import PublicDemo from './public-demo/PublicDemo';
import { PublicDemoDetail } from './public-demo/PublicDemoDetail';

const withSideBar = (comp: ReactNode) => {
  return (
    <>
      <SideBar />
      {comp}
    </>
  );
};

const topPageWrapper = (children: ReactNode) => {
  return (
    <div className="flex w-full h-full overflow-hidden">
      <Layout className="bg-white overflow-auto">
        <Row className="flex mt-7 justify-center">
          <Col span={20}>{children}</Col>
        </Row>
      </Layout>
    </div>
  );
};

const filePageWrapper = (children: ReactNode) => {
  return <div className="flex-col w-full">{children}</div>;
};

const App = () => {
  const currentUser = useCurrentUser();
  const authLoading = useAuthenticationLoading();
  return (
    <Layout>
      <Router>
        {currentUser ? (
          <Layout style={{ height: '100vh' }} className="border-0 border-t-2 border-solid border-toolbox-primary">
            <Switch>
              <Route path={'/demo/:algorithmId/models/:version?'}>
                {withSideBar(topPageWrapper(<DemoOverview />))}
              </Route>
              <Route path={'/demo/:algorithmId/:inferenceId/result'}>{filePageWrapper(<DemoResult />)}</Route>
              <Route path={'/custom/:trainingId/result'}>{filePageWrapper(<ReviewResult />)}</Route>
              <Route path={'/custom/:algorithmId/model/:modelId'}>{withSideBar(topPageWrapper(<ModelDetail />))}</Route>
              <Route path={'/custom/:algorithmId/models'}>{withSideBar(topPageWrapper(<ModelOverview />))}</Route>
              <Route path={'/projects/:projectId/datasets/:datasetId/dataset-file/:fileId'}>
                {filePageWrapper(<FileOverview />)}
              </Route>
              <Route path={'/projects/:projectId/datasets/:datasetId/overview'}>
                {withSideBar(topPageWrapper(<DatasetOverview />))}
              </Route>
              <Route path={'/projects/:projectId/datasets/:datasetId/report'}>
                {withSideBar(topPageWrapper(<DatasetReport />))}
              </Route>
              <Route path={'/projects/:projectId/datasets'}>{withSideBar(topPageWrapper(<ProjectOverview />))}</Route>
              <Route path={'/:type/:algorithmId/model/:modelId/:fileId'}>
                {filePageWrapper(<FlowInferenceResult />)}
              </Route>
              <Route path={'/share/:algorithmId/:flowId'}>{topPageWrapper(<FlowDetail demo={true} />)}</Route>
              <Route path={'/flows/:algorithmId/:flowId'}>
                {withSideBar(topPageWrapper(<FlowDetail demo={false} />))}
              </Route>
              <Route path={'/flows/:algorithmId'}>{withSideBar(topPageWrapper(<FlowOverview />))}</Route>
              <Route path={'/inbox/:clientName/:doctype/overview'}>
                {withSideBar(topPageWrapper(<InboxsetOverview />))}
              </Route>
              <Route path={'/inbox/:clientName/:doctype/file/:fileId'}>{filePageWrapper(<InboxFileOverview />)}</Route>
              <Route path={'/inbox/:clientName'}>{withSideBar(topPageWrapper(<ClientOverview />))}</Route>
              <Route path={'/swagger-api'}>{withSideBar(<IntroductionPage />)}</Route>
              <Route path={'/overview/:algorithmVersionId/datasets/:datasetVersionId/dataset-file/:fileId'}>
                {filePageWrapper(<DetailTestResult />)}
              </Route>
              <Route path={'/overview/:algorithmVersionId/datasets/:datasetVersionId/'}>
                {withSideBar(topPageWrapper(<ScoreDatasetOverview />))}
              </Route>
              <Route path={'/overview/:algorithmId'}>{withSideBar(topPageWrapper(<AlgorithmDetailOverview />))}</Route>
              <Route path={'/overview'}>{withSideBar(topPageWrapper(<ScoreOverview />))}</Route>
              <Route path={'/admin'}>{withSideBar(topPageWrapper(<Admin />))}</Route>
              <Route path={'/'}>{withSideBar(<IntroductionPage />)}</Route>
            </Switch>
          </Layout>
        ) : authLoading ? (
          <Layout style={{ height: '100vh' }} className="border-0 border-t-2 border-solid border-toolbox-primary">
            <div className="flex h-full">
              <Spinner />
            </div>
          </Layout>
        ) : (
          <>
            <Switch>
              <Route path={'/document-recognition/detail/:inferenceId'}>
                <Layout style={{ height: '100vh' }}>{filePageWrapper(<PublicDemoDetail />)}</Layout>
              </Route>
              <Route path={'/document-recognition/:ln'} exact>
                <Layout style={{ height: '100vh' }}>
                  <PublicDemo />
                </Layout>
              </Route>
              <Route path={'/document-recognition'} exact>
                <Layout style={{ height: '100vh' }}>
                  <PublicDemo />
                </Layout>
              </Route>
              <Route
                path={[
                  '/',
                  '/forgot-password',
                  '/new-password-required',
                  '/forgot-password-finish',
                  '/share/:algorithmId/:flowId',
                ]}
                exact>
                <Layout style={{ height: '100vh' }} className="border-0 border-t-2 border-solid border-toolbox-primary">
                  <SignIn />
                </Layout>
              </Route>
              <Redirect
                to={{
                  pathname: '/',
                }}
              />
            </Switch>
          </>
        )}
      </Router>
    </Layout>
  );
};

export default App;
